import { Suspense, lazy, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { DebugLevel } from './_configs/types';
import { store } from './_flux/Store';
import CommonProvider from './provider/CommonProvider';
import ConfigCheckProvider from './provider/ConfigCheckProvider';
import ErrorHandler from './provider/ErrorHandler';
import type { AppContainer } from './types';
import checkUndefinedNullOrEmpty from './utils/tools/checkUndefinedNullOrEmpty';
import { LIVE } from './vars';

const App = lazy(() => import('./App' /* webpackChunkName: "App" */));
const AnalyticsProvider = lazy(() => import('./provider/analytics/provider' /* webpackChunkName: "AnalyticsProvider" */));
const AutoUncleProvider = lazy(() => import('./provider/autouncle/provider' /* webpackChunkName: "AutoUncleProvider" */));
const BrowserRouter = lazy(async () => ({ default: (await import('react-router' /* webpackChunkName: "ReactRouterDom_BrowserRouter" */))?.BrowserRouter }));
const OldApp = lazy(() => import('./oldApp' /* webpackChunkName: "OldApp" */));
const WebcalcProvider = lazy(() => import('./provider/webcalc/provider' /* webpackChunkName: "WebcalcProvider" */));

function Entry(container: AppContainer) {
    const { i18n } = useTranslation();

    useEffect(() => {
        i18n?.changeLanguage?.(container?.settings?.general?.language);
    }, [container?.settings?.general?.language, i18n]);

    const autoUnclePriceRatingEnabled = useMemo(
        () =>
            (container?.settings?.general?.debug === DebugLevel.DEBUG || LIVE) &&
            container?.settings?.api?.autoUncle?.priceRating === true &&
            !checkUndefinedNullOrEmpty(container?.settings?.api?.autoUncle?.sourceName),
        [container?.settings?.api?.autoUncle?.priceRating, container?.settings?.api?.autoUncle?.sourceName, container?.settings?.general?.debug]
    );

    const trackingEnabled = useMemo(
        () => container?.settings?.api?.tracking?.enabled === true && (container?.settings?.general?.debug === DebugLevel.DEBUG || LIVE),
        [container?.settings?.api?.tracking?.enabled, container?.settings?.general?.debug]
    );

    const webcalcEnabled = useMemo(() => container?.settings?.api?.webcalc?.enabled === true, [container?.settings?.api?.webcalc?.enabled]);

    return (
        <CommonProvider
            allFaarenVehicles={container?.allFaarenVehicles}
            module={container?.appProps?.module}
            rawAllFacets={container?.allFacets}
            rootContainer={container?.rootContainer}
            settings={container?.settings}
            Skeleton={container?.Skeleton}
            SkeletonDetails={container?.SkeletonDetails}
        >
            <Provider store={store}>
                <ErrorHandler module={container?.appProps?.module}>
                    <ConfigCheckProvider module={container?.appProps?.module}>
                        {trackingEnabled && <AnalyticsProvider />}
                        {autoUnclePriceRatingEnabled && <AutoUncleProvider />}
                        {webcalcEnabled && <WebcalcProvider />}
                        <Suspense fallback={location?.search?.includes('_carId') || location?.pathname?.includes('/c/') ? container?.SkeletonDetails : container?.Skeleton}>
                            {container?.settings?.general?.useOldRouter === true ? (
                                <BrowserRouter>
                                    <OldApp {...container?.appProps} />
                                </BrowserRouter>
                            ) : (
                                <App {...container?.appProps} />
                            )}
                        </Suspense>
                    </ConfigCheckProvider>
                </ErrorHandler>
            </Provider>
        </CommonProvider>
    );
}

export default memo(Entry);
